import { InteractionRequiredAuthError, type AccountInfo } from '@azure/msal-browser'
import { msalInstance } from '@/auth/msalConfig'
import { parseSseStream } from './sseParser'
import type { StreamEvent } from './streamReducer'
import type { DispatchedToolResult } from './toolDispatcher'
import type { ChatTransport, TurnContext } from './transport'
import { buildUserContextLine } from './userContext'

/**
 * `ChatTransport` for the Azure AI Foundry Agent Service.
 *
 * Foundry exposes agents through the OpenAI Responses API. Conversations
 * chain by passing `previous_response_id` on subsequent turns rather than
 * maintaining a server-side thread, so this transport is stateful w.r.t.
 * `threadId` and ignores `ctx.history`. Everything Responses-API-specific —
 * the endpoint, the request shapes, the Foundry-scoped token, the SSE event
 * names (via `parseSseStream`) — lives here; the hook and reducer never see
 * it.
 */

const FOUNDRY_SCOPES = ['https://ai.azure.com/.default']

/**
 * The v1 path is the version selector — the endpoint has no `api-version`
 * query parameter.
 *
 *   POST {projectEndpoint}/openai/v1/responses
 *   https://learn.microsoft.com/rest/api/aifoundry/aiproject#responses
 */
const RESPONSES_PATH = '/openai/v1/responses'

const projectEndpoint = import.meta.env.VITE_FOUNDRY_PROJECT_ENDPOINT
const agentId = import.meta.env.VITE_FOUNDRY_AGENT_ID

// ---------------------------------------------------------------------------
// Responses API request shapes
// ---------------------------------------------------------------------------

type ApprovalInputItem = {
  type: 'mcp_approval_response'
  approval_request_id: string
  approve: boolean
}

type MessageInputItem = {
  type: 'message'
  role: 'user' | 'developer' | 'system'
  content: Array<{ type: 'input_text'; text: string }>
}

/**
 * Function-tool result echo. The Responses API expects one of these per
 * pending tool call when the run resumes after `requires_action`.
 */
type FunctionCallOutputItem = {
  type: 'function_call_output'
  call_id: string
  output: string
}

interface ResponsesRequest {
  agent_reference: { type: 'agent_reference'; name: string }
  input: string | Array<ApprovalInputItem | MessageInputItem | FunctionCallOutputItem>
  stream: boolean
  previous_response_id?: string
}

// ---------------------------------------------------------------------------
// Transport
// ---------------------------------------------------------------------------

export const foundryTransport: ChatTransport = {
  configMissing: !projectEndpoint || !agentId,

  async *startTurn(message: string, ctx: TurnContext) {
    yield* withFoundryToken(ctx, () => buildTurnBody(message, ctx))
  },

  async *submitToolOutputs(results: DispatchedToolResult[], ctx: TurnContext) {
    yield* withFoundryToken(ctx, () =>
      withChain(
        {
          agent_reference: agentRef(),
          input: results.map(r => ({
            type: 'function_call_output' as const,
            call_id: r.callId,
            output: r.output,
          })),
          stream: true,
        },
        ctx.threadId,
      ),
    )
  },

  async *respondToApproval(approvalRequestId: string, approve: boolean, ctx: TurnContext) {
    yield* withFoundryToken(ctx, () =>
      withChain(
        {
          agent_reference: agentRef(),
          input: [
            { type: 'mcp_approval_response', approval_request_id: approvalRequestId, approve },
          ],
          stream: true,
        },
        ctx.threadId,
      ),
    )
  },

  async *resumeAfterConsent(lastMessage: string, ctx: TurnContext) {
    yield* withFoundryToken(ctx, () =>
      withChain({ agent_reference: agentRef(), input: lastMessage, stream: true }, ctx.threadId),
    )
  },
}

// ---------------------------------------------------------------------------
// Request building
// ---------------------------------------------------------------------------

function agentRef(): ResponsesRequest['agent_reference'] {
  return { type: 'agent_reference', name: agentId as string }
}

/** Sets `previous_response_id` only when a chain token exists (never assigns undefined). */
function withChain(body: ResponsesRequest, threadId: string | null): ResponsesRequest {
  if (threadId) body.previous_response_id = threadId
  return body
}

/**
 * First turn of a new chain includes a developer-role context item so the
 * agent knows who it's talking to; subsequent turns inherit it via
 * `previous_response_id` — no need to repeat. The MSAL access token already
 * encodes the same identity, but the model can't read JWT claims, so we
 * surface name + UPN inline.
 */
function buildTurnBody(message: string, ctx: TurnContext): ResponsesRequest {
  const isFirstTurn = !ctx.threadId
  const contextLine = isFirstTurn ? buildUserContextLine(ctx.account) : null
  const input: ResponsesRequest['input'] = contextLine
    ? [
        {
          type: 'message',
          role: 'developer',
          content: [{ type: 'input_text', text: contextLine }],
        },
        {
          type: 'message',
          role: 'user',
          content: [{ type: 'input_text', text: message }],
        },
      ]
    : message
  return withChain({ agent_reference: agentRef(), input, stream: true }, ctx.threadId)
}

// ---------------------------------------------------------------------------
// Networking
// ---------------------------------------------------------------------------

/**
 * Acquire the Foundry-scoped token, then stream the given request. On token
 * failure yields a single `stream.error` and stops — never throws, so the
 * hook's consume loop stays uniform.
 */
async function* withFoundryToken(
  ctx: TurnContext,
  makeBody: () => ResponsesRequest,
): AsyncGenerator<StreamEvent> {
  let token: string
  try {
    token = await acquireFoundryToken(ctx.account)
  } catch (err) {
    yield {
      kind: 'stream.error',
      message: err instanceof Error ? err.message : 'Token acquisition failed',
    }
    return
  }
  yield* streamResponse(makeBody(), token, ctx.signal)
}

/**
 * POST the request and yield parsed `StreamEvent`s. Network / non-2xx /
 * empty-body failures surface as a single `stream.error` event; an aborted
 * request ends the stream silently (the user cancelled or reset). Never
 * throws.
 */
async function* streamResponse(
  body: ResponsesRequest,
  token: string,
  signal: AbortSignal,
): AsyncGenerator<StreamEvent> {
  try {
    const response = await fetch(`${projectEndpoint}${RESPONSES_PATH}`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
        Accept: 'text/event-stream',
      },
      body: JSON.stringify(body),
      signal,
    })

    if (!response.ok) {
      const errBody = await response.text().catch(() => '')
      yield {
        kind: 'stream.error',
        message: `Foundry ${response.status}: ${errBody.slice(0, 200) || response.statusText}`,
      }
      return
    }

    if (!response.body) {
      yield { kind: 'stream.error', message: 'Response body is empty' }
      return
    }

    for await (const event of parseSseStream(response.body)) {
      if (signal.aborted) break
      yield event
    }
  } catch (err) {
    if (err instanceof Error && err.name === 'AbortError') return
    yield {
      kind: 'stream.error',
      message: err instanceof Error ? err.message : 'Stream failed',
    }
  }
}

async function acquireFoundryToken(account: AccountInfo): Promise<string> {
  try {
    const result = await msalInstance.acquireTokenSilent({
      scopes: FOUNDRY_SCOPES,
      account,
    })
    return result.accessToken
  } catch (err) {
    if (err instanceof InteractionRequiredAuthError) {
      // First-time Foundry scope: user hasn't consented yet. Use a popup
      // rather than acquireTokenRedirect so the chat state survives — the
      // redirect alternative full-page-navigates away, loses the typed
      // message + thread, and on return the SPA boots fresh. Popup keeps
      // the SPA tab alive.
      const result = await msalInstance.acquireTokenPopup({
        scopes: FOUNDRY_SCOPES,
        account,
      })
      return result.accessToken
    }
    throw err
  }
}
